/**
 * In-context permission rationale modal.
 *
 * Shown the first time a user taps a "Current location" / "Camera"
 * button in the app. Renders one of three states:
 *   * ``prompt``  — first request, big "Allow" CTA that triggers the
 *                   native OS dialog via the caller's ``onRequest``.
 *   * ``denied``  — explains how to re-enable from device Settings;
 *                   offers a deep link via ``openAppSettings`` where
 *                   the platform supports it.
 *   * ``error``   — generic failure copy with retry.
 *
 * Play Store / App Store reviewers specifically look for this kind
 * of in-context rationale. The first-launch ``/onboarding/permissions``
 * screen (iter47) is the up-front explainer; THIS modal is the
 * inline-at-feature-use explainer that complements it.
 */
import { MapPin, Camera, Bell, ShieldCheck, Settings, X, Loader2 } from "lucide-react";
import { useState } from "react";
import { openAppSettings } from "../lib/geolocation";

const ICONS = { location: MapPin, camera: Camera, notifications: Bell };

const COPY = {
    location: {
        title: "Allow location access",
        prompt: "We use your location to pin your delivery address, show shops near you, and calculate delivery fees accurately.",
        denied: "Location is currently blocked. Open device Settings → Apps → Bizil → Permissions → Location, then choose \"Allow while using the app\".",
        ctaPrompt: "Continue",
        whyWeAsk: "Without location, you'll have to type your address by hand each time — and we can't auto-check the delivery zone.",
    },
    camera: {
        title: "Allow camera access",
        prompt: "We use the camera so you can take and upload photos of your shop, menu items, or profile picture.",
        denied: "Camera is currently blocked. Open device Settings → Apps → Bizil → Permissions → Camera and enable it.",
        ctaPrompt: "Continue",
        whyWeAsk: "Without camera access, you'll need to pick an existing photo from your gallery instead of taking a new one.",
    },
    notifications: {
        title: "Allow notifications",
        prompt: "We send order updates, delivery alerts, and important account notices. You can turn off promotional notifications any time from Profile.",
        denied: "Notifications are currently blocked. Open device Settings → Apps → Bizil → Notifications and enable them.",
        ctaPrompt: "Continue",
        whyWeAsk: "Without notifications, you'll need to keep re-opening the app to check order status, delivery updates, and offers.",
    },
};

export default function PermissionGateModal({
    permission = "location",
    state = "prompt",         // "prompt" | "denied" | "error"
    onRequest,                // async () => "granted"|"denied" — caller drives the native API
    onClose,
    onGranted,                // called when state flips to "granted"
}) {
    const [busy, setBusy] = useState(false);
    const [openingSettings, setOpeningSettings] = useState(false);
    const Icon = ICONS[permission] || ShieldCheck;
    const copy = COPY[permission] || COPY.location;

    const handleAllow = async () => {
        setBusy(true);
        try {
            const next = await onRequest?.();
            // onRequest implementations vary: some return a bare state
            // string, others a structured {state, coords, error} object.
            const state = typeof next === "string" ? next : next?.state;
            if (state === "granted") {
                onGranted?.();
                onClose?.();
            }
            // For "denied" / "error" the parent re-renders us with the
            // new state — no extra handling needed here.
        } finally { setBusy(false); }
    };

    const handleOpenSettings = async () => {
        setOpeningSettings(true);
        const ok = await openAppSettings();
        setOpeningSettings(false);
        if (!ok) {
            // On web / when no deep-link plugin is wired we just close
            // the modal — the inline copy already tells the user where
            // to go.
            onClose?.();
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] bg-black/55 flex items-end sm:items-center justify-center p-3"
            data-testid={`permission-gate-${permission}`}
            onClick={onClose}
        >
            <div
                className="bg-card rounded-3xl w-full max-w-sm p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    data-testid="permission-gate-close"
                    className="absolute top-3 right-3 p-1.5 rounded-full text-muted-foreground hover:bg-muted"
                ><X size={16} /></button>

                <div className="text-center pt-2">
                    <div className="inline-flex w-16 h-16 rounded-2xl bg-primary/10 items-center justify-center mb-3">
                        <Icon size={30} className="text-primary" />
                    </div>
                    <h2 className="text-lg font-extrabold tracking-tight">{copy.title}</h2>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        {state === "denied" ? copy.denied : copy.prompt}
                    </p>
                    {state === "denied" && (
                        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 rounded-xl p-3 mt-3 text-left">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900 dark:text-amber-300 mb-1">Why we ask</p>
                            <p className="text-xs text-amber-900/90 dark:text-amber-200/90">
                                {copy.whyWeAsk}
                            </p>
                        </div>
                    )}
                </div>

                <div className="mt-5 space-y-2.5">
                    {state !== "denied" && (
                        <button
                            data-testid="permission-gate-allow"
                            onClick={handleAllow}
                            disabled={busy}
                            className="w-full bg-primary text-primary-foreground rounded-2xl py-3.5 font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-60"
                        >
                            {busy && <Loader2 size={16} className="animate-spin" />}
                            {copy.ctaPrompt}
                        </button>
                    )}
                    {state === "denied" && (
                        <button
                            data-testid="permission-gate-open-settings"
                            onClick={handleOpenSettings}
                            disabled={openingSettings}
                            className="w-full bg-primary text-primary-foreground rounded-2xl py-3.5 font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-60"
                        >
                            {openingSettings ? <Loader2 size={16} className="animate-spin" /> : <Settings size={16} />}
                            Open device settings
                        </button>
                    )}
                    {/*
                      * iter98 — App Store Guideline 5.1.1(iv) compliance.
                      * Removed the secondary "Not now" text button from
                      * the pre-permission rationale. Apple now requires
                      * that ANY message preceding a permission request
                      * MUST always proceed to the OS prompt — no
                      * mid-flow dismiss. The top-right "X" close remains
                      * because this modal is user-initiated (the user
                      * tapped a feature that needs the permission);
                      * if they want out, they simply abandon the action.
                      * DO NOT re-introduce a "Not now" / "Skip" / "Maybe
                      * later" button here.
                      */}
                </div>
            </div>
        </div>
    );
}
